import { createSelector } from '@reduxjs/toolkit'

import { errorView, queryView, type QueryResultLike } from '@krp/client-runtime'

import { grantsOf, SEARCH_TEST } from '../../access/model/grants'
import type { MyAccess } from '../../access/model/types'
import { commonPhotoThumbUrl } from '../../common-photos/api/urls'
import type { ResourceSummary } from '../../resources/model/types'
import type { MutationResultLike } from '../../../shared/views'
import { disagreementOf, evidenceEffect, evidenceLabel, evidenceNote, photoCheckOf } from './evidenceLabels'
import { stepPercent } from './variants'
import type {
  HealthLevel,
  HealthVm,
  HitVm,
  SearchHealth,
  SearchHit,
  SearchOutcomeVm,
  SearchResponse,
  SearchVm,
  VerdictOutcomeVm,
} from './types'

/**
 * Именованные селекторы поиска. Главное различие экрана: «не нашлось» (200,
 * пустая выдача) и «не смогли искать» (503, сбой службы) — разные состояния с
 * разным текстом и значком; пустой список при сбое показывать нельзя.
 */
export interface SearchVmInput {
  health: QueryResultLike<SearchHealth>
  /** Результат последнего поиска (кэш мутации по постоянному ключу окна). */
  search: MutationResultLike<SearchResponse>
  resources: QueryResultLike<readonly ResourceSummary[]>
  /** Мои права: служебные звенья и проверка видны только с правом `platform:search:test`. */
  access: QueryResultLike<MyAccess>
  /** Предварительная выдача идущего поиска (событие потока `preliminary`); нет — `null`. */
  preliminary?: SearchResponse | null | undefined
}

const EMPTY_RESOURCES: readonly ResourceSummary[] = []

const COMPONENT_LABELS: Record<string, string> = {
  ok: 'работает',
  unavailable: 'недоступно',
  'not-configured': 'не настроено',
  misconfigured: 'настроено неверно',
  incompatible: 'несовместимо',
}

const HEALTH_LABELS: Record<HealthLevel, string> = {
  unknown: 'Состояние не проверено',
  checking: 'Проверяем…',
  ok: 'Поиск работает',
  degraded: 'Поиск работает не полностью или недоступен',
  'not-configured': 'Поиск не настроен',
  unreachable: 'Состояние поиска не получено',
  forbidden: 'Нет доступа к состоянию поиска',
}

function toHealth(result: QueryResultLike<SearchHealth>): HealthVm {
  const view = queryView(
    {
      isUninitialized: result.isUninitialized,
      isLoading: result.isLoading,
      isError: result.isError,
      isSuccess: result.isSuccess,
      ...(result.error !== undefined ? { error: result.error } : {}),
      ...(result.data !== undefined ? { data: result.data } : {}),
    },
    () => false,
  )
  const make = (level: HealthLevel, detail: string | null = null, components: HealthVm['components'] = []): HealthVm => ({
    level,
    label: HEALTH_LABELS[level],
    detail,
    components,
  })
  switch (view.status) {
    case 'idle':
      return make('unknown')
    case 'loading':
      return make('checking')
    case 'forbidden':
      return make('forbidden', view.error?.message ?? null)
    case 'unavailable':
      // 503 здоровья несёт и звенья, но transport отдаёт только текст: звенья — в журнале Platform.
      return make('degraded', view.error?.message ?? null)
    case 'unauthorized':
    case 'error':
      return make('unreachable', view.error?.message ?? null)
    default: {
      const data = result.data
      if (data === undefined) return make('unknown')
      const level: HealthLevel = data.status === 'ok' ? 'ok' : data.status === 'not-configured' ? 'not-configured' : 'degraded'
      return make(
        level,
        null,
        data.components.map((component) => ({
          name: component.name,
          label: COMPONENT_LABELS[component.status] ?? component.status,
          detail: component.detail,
        })),
      )
    }
  }
}

/**
 * Процент совпадения целым числом для угла плашки. Шкала — `FusedHit.Confidence` 0…1 в Platform.Application/Search/PhotoSearchFusion.cs:
 * единая для порядка выдачи и показа, шаг 0,01. Процент — число × 100, ограниченное 0…100; не число — `null` (процент не выдумывается).
 * Слов уровня («высокая», «средняя») и объяснений нет: показывается только число.
 */
export const confidencePercent = (value: number): number | null => (Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value * 100))) : null)

/** По чему найден товар — словами: человек сверяет это с прочитанным на фото. Неизвестная дорожка показывается как есть. */
const MATCHED_BY: Readonly<Record<string, string>> = { barcode: 'штрихкод', text: 'текст этикетки', visual: 'фото' }
const matchedByText = (tracks: readonly string[]): string => tracks.map((track) => MATCHED_BY[track] ?? track).join(', ')

function toHits(hits: readonly SearchHit[], resources: readonly ResourceSummary[]): HitVm[] {
  const names = new Map(resources.map((resource) => [resource.id.toLowerCase(), resource.displayName]))
  return hits.map((hit) => ({
    productId: hit.productId,
    name: hit.name,
    sku: hit.sku,
    barcode: hit.barcode,
    matchPercent: confidencePercent(hit.confidence),
    matchedBy: matchedByText(hit.matchedBy),
    stepPercent: stepPercent(hit.confidence),
    commonPhotoUrl: commonPhotoThumbUrl(hit.productId, hit.mainPhoto),
    photoCheck: photoCheckOf(hit),
    evidence: (hit.evidence ?? []).map((item) => ({
      kind: item.kind,
      label: evidenceLabel(item.kind),
      note: evidenceNote(item.note),
      present: item.present,
      // Нет данных по дорожке — это «нет данных», а не минус: влияние `none`, а не `against`.
      effect: evidenceEffect(item),
    })),
    twinGroup: hit.twinGroup ?? null,
    disagreement: disagreementOf(hit),
    identities: hit.identities.map((identity) => {
      const name = names.get(identity.resourceId.toLowerCase())
      return {
        key: `${identity.resourceId}:${identity.remoteKey}`,
        resourceId: identity.resourceId,
        resourceKind: identity.resourceKind,
        resource: name ?? `Неизвестный ресурс (${identity.resourceKind})`,
        resourceKnown: name !== undefined,
        remoteKey: identity.remoteKey,
        state: identity.state,
      }
    }),
  }))
}

/**
 * Варианты серии близнецов с отличиями (RP-FB66). Экран «Выберите вариант» открывает сам сервер: вердикт `candidates` с причиной
 * `similar-variants`. Серия — это позиции ответа с тем же `twinGroup.id`, что и у лидера выдачи; отличия каждой лежат в её `twinGroup`.
 * Позиций серии меньше двух (остальные членов не попали в выдачу) — экрана нет, членов не выдумываем. Слабые кандидаты ниже порога в серию
 * не входят: они «вероятнее всего не он».
 */
function toTwin(data: SearchResponse, resources: readonly ResourceSummary[]): readonly HitVm[] | undefined {
  if (data.verdict !== 'candidates' || !(data.reasons ?? []).some((reason) => reason.code === 'similar-variants')) return undefined
  const shown = [...(data.groups?.confident ?? []), ...(data.groups?.possible ?? [])]
  const seriesId = shown[0]?.twinGroup?.id
  if (seriesId === undefined) return undefined
  const members = new Map<string, SearchHit>()
  for (const hit of shown) {
    if (hit.twinGroup?.id === seriesId && !members.has(hit.productId)) members.set(hit.productId, hit)
  }
  return members.size >= 2 ? toHits([...members.values()], resources) : undefined
}

/** Запрос прерван человеком: RTK отдаёт `AbortError`, наш transport — код `aborted`. */
function isAbort(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  return ('name' in error && error.name === 'AbortError') || ('code' in error && error.code === 'aborted')
}

/** Ответ сервера с вердиктом → модель вида; ответ без вердикта (прежний сервер) — `null`. */
function toVerdictOutcome(data: SearchResponse, resources: readonly ResourceSummary[]): VerdictOutcomeVm | null {
  if (data.verdict === undefined || data.verdict === null) return null
  return {
    kind: 'verdict',
    verdict: data.verdict,
    confident: toHits(data.groups?.confident ?? [], resources),
    possible: toHits(data.groups?.possible ?? [], resources),
    weak: toHits(data.groups?.weak ?? [], resources),
    listMinPercent: data.groups?.listMin === undefined || data.groups.listMin === null ? null : confidencePercent(data.groups.listMin),
    hints: data.hints ?? null,
    total: data.total,
    unresolved: data.unresolved,
    recognized: data.recognized,
    twin: toTwin(data, resources),
    reasons: data.reasons,
    searchId: data.searchId ?? null,
  }
}

function toOutcome(result: MutationResultLike<SearchResponse>, resources: readonly ResourceSummary[], preliminaryData: SearchResponse | null): SearchOutcomeVm {
  const preliminary = preliminaryData === null ? undefined : (toVerdictOutcome(preliminaryData, resources) ?? undefined)
  if (result.isError && result.error !== undefined) {
    // Отмена кнопкой «Отменить» - не сбой: прерванный запрос RTK возвращает AbortError, наш transport - код `aborted`.
    if (isAbort(result.error)) return { kind: 'cancelled' }
    const error = errorView(result.error)
    const degraded = error.kind === 'unavailable'
    const title =
      error.kind === 'unavailable'
        ? 'Поиск сейчас недоступен'
        : error.kind === 'forbidden'
          ? 'Нет доступа к поиску'
          : error.kind === 'unauthorized'
            ? 'Сессия завершена'
            : error.kind === 'validation'
              ? 'Фото не принято'
              : error.kind === 'unknown-outcome'
                ? 'Ответ не получен'
                : 'Не удалось выполнить поиск'
    const retryable = error.kind === 'unavailable' || error.kind === 'network' || error.kind === 'timeout' || error.kind === 'unknown-outcome' ||
      error.code === 'stream-incomplete'
    return { kind: 'failed', degraded, title, message: error.message, code: error.code, correlationId: error.correlationId, retryable, ...(preliminary === undefined ? {} : { preliminary }) }
  }
  if (result.isLoading) return preliminary === undefined ? { kind: 'searching' } : { kind: 'searching', preliminary }
  if (result.data === undefined) return { kind: 'idle' }
  const data = result.data
  // Вердикт сервера (RP-FB56): экран показывает один из четырёх ответов.
  const verdict = toVerdictOutcome(data, resources)
  if (verdict !== null) return verdict
  // Сервер всегда отдаёт вердикт; ответ без него — не «ничего не нашлось», а сбой договора, и человек должен увидеть его, а не пустую выдачу.
  return {
    kind: 'failed', degraded: false, title: 'Не удалось выполнить поиск', message: 'Сервер не вернул вердикт поиска. Повторите поиск.',
    code: 'no-verdict', correlationId: null, retryable: true,
  }
}

export const makeSelectSearchVm = () =>
  createSelector(
    [
      (input: SearchVmInput) => input.health,
      (input: SearchVmInput) => input.search,
      (input: SearchVmInput) => input.resources.data ?? EMPTY_RESOURCES,
      (input: SearchVmInput) => grantsOf(input.access).permissions.has(SEARCH_TEST),
      // Администратор системы — роль `system_admin` из «моих прав» (суперроль, `SUPER_ROLE`), а не право: подробности поиска служебные.
      (input: SearchVmInput) => grantsOf(input.access).superRole,
      (input: SearchVmInput) => input.preliminary ?? null,
    ],
    (health, search, resources, canTest, isSystemAdmin, preliminary): SearchVm => {
      return {
        canDiagnose: canTest,
        isSystemAdmin,
        health: toHealth(health),
        outcome: toOutcome(search, resources, preliminary),
      }
    },
  )
